import { Injectable } from '@angular/core';
import {KeishaHibetsuFile, Temperature} from '../../shared/interfaces/keisha-hibetsu-file';
import {
  ABNORMAL_PROPERTIES,
  COLLECTION_NAME,
  KG_TO_G,
  METER_SHUBETSU,
  SEIJOU,
  TAIOU_PROPERTIES
} from '../../shared/constants';
import {WithNullable, yyyyMMdd} from '../../shared/typeAliases';
import {CommonService} from './common.service';
import {Feed, Feed2} from '../../shared/interfaces/feed';
import {MasterService} from './master.service';
import {Keisha} from '../../shared/interfaces/keisha';
import {TentouShoutouJikoku} from '../../shared/interfaces/tentou';
import {firstValueFrom, forkJoin, Observable, of, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';
import {ShiyoulotHibetsuFile} from '../../shared/interfaces/shiyoulot-hibetsu-file';
import firebase from 'firebase';
import firestore = firebase.firestore;
import {AngularFirestore} from '@angular/fire/firestore';
import {SessionService} from './session.service';

@Injectable({
  providedIn: 'root'
})
export class KeishaHibetsuFileService {
  constructor(private commonService: CommonService,
              private masterService: MasterService,
              private sessionService: SessionService,
              private db: AngularFirestore,
              ) { }

  /**
   *
   * @param keishaId
   * @param today
   */
  getInitKeishaHibetsuFile(keishaId: string, today = this.commonService.getDateWithString()): KeishaHibetsuFile {
    return getInitKeishaHibetsuFile(keishaId, today);
  }


  /**
   * 鶏舎日別ファイルを取得
   * 鶏舎日別ファイルの現在羽数を使う場合、getKeishaHibetsuFileUpdatedGenzaiWasuuを使う
   * 鶏舎日別ファイルの現在羽数を使わない場合、getKeishaHibetsuFileを使う
   *
   * @param keishaId
   * @param fromDate
   * @param toDate
   */
  getKeishaHibetsuFile(keishaId: string, fromDate: string, toDate: string) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return firstValueFrom(
      this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
        .collection(COLLECTION_NAME.KEISHAMASTER).doc(keishaId)
        .collection(COLLECTION_NAME.KEISHAHIBETSUFILE, ref => {
          let query:
            | firestore.CollectionReference
            | firestore.Query = ref;
          query = query.where('id', '>=', fromDate);
          query = query.where('id', '<=', toDate);
          return query;
        })
        .get()
    );
  }

  /**
   * 鶏舎日別ファイルのobservableを取得
   *
   * @param keishaId
   * @param fromDate
   * @param toDate
   */
  getKeishaHibetsuFileObservable(keishaId: string, fromDate: string, toDate: string) {
    const customerId = this.sessionService.session.personalSetting.customerId;
    return this.db.collection(COLLECTION_NAME.CUSTOMERS).doc(customerId)
      .collection(COLLECTION_NAME.KEISHAMASTER).doc(keishaId)
      .collection(COLLECTION_NAME.KEISHAHIBETSUFILE, ref => {
        let query:
          | firestore.CollectionReference
          | firestore.Query = ref;
        query = query.where('id', '>=', fromDate);
        query = query.where('id', '<=', toDate);
        return query;
      }).valueChanges({idField: 'id'}) as Observable<KeishaHibetsuFile[]>;
  }

  /**
   * 鶏舎にいる飼養ロット日別ファイルの羽数合計から算出した現在羽数を
   * 鶏舎日別ファイルにセットして返す
   * 鶏舎日別ファイルの現在羽数を使う場合、getKeishaHibetsuFileUpdatedGenzaiWasuuを使う
   * 鶏舎日別ファイルの現在羽数を使わない場合、getKeishaHibetsuFileを使う
   *
   * @param keishaId
   * @param fromDate
   * @param toDate
   */
  getKeishaHibetsuFileUpdatedGenzaiWasuu(keishaId: string, fromDate: string, toDate: string) {
    const keishaHibetsuFile$ = this.getKeishaHibetsuFileObservable(keishaId, fromDate, toDate);
    return keishaHibetsuFile$.pipe(
      switchMap(keishaHibetsuFiles => {
        // 鶏舎日別Fがない場合、空配列を返す
        if (keishaHibetsuFiles.length === 0) {
          return of([]);
        }
        return forkJoin(
          keishaHibetsuFiles.map(keishaHibetsuFile =>
            this.getShiyoulotHibetsuFileInSameKeisha(keishaId, keishaHibetsuFile.id).pipe(
              map(shiyoulotHibetsuFileDocs => {
                const shiyoulotHibetsuFiles = shiyoulotHibetsuFileDocs.docs.map(doc => doc.data()) as ShiyoulotHibetsuFile[];
                // 鶏舎日別ファイルの斃死、訂正、淘汰、現在羽数を飼養ロット日別Fから算出
                const wasuuPropsForKeishaHibetsuFile = {
                  genzaiWasuu: 0,
                  heishiWasuu: 0,
                  teiseiWasuu: 0,
                  toutaWasuu: 0,
                };
                shiyoulotHibetsuFiles.forEach(shiyoulotHibetsuFile => {
                  const genzaiWasuu = this.commonService.calcGenzaiWasuuForShiyoulotHibetsuFile(shiyoulotHibetsuFile);
                  const isPositiveGenzaiWasuu = genzaiWasuu > 0;
                  // 現在羽数が0以上の場合のみ加算
                  if (isPositiveGenzaiWasuu) {
                    wasuuPropsForKeishaHibetsuFile.genzaiWasuu += genzaiWasuu;
                    wasuuPropsForKeishaHibetsuFile.heishiWasuu += shiyoulotHibetsuFile.heishiWasuu;
                    wasuuPropsForKeishaHibetsuFile.teiseiWasuu += shiyoulotHibetsuFile.teiseiWasuu;
                    wasuuPropsForKeishaHibetsuFile.toutaWasuu += this.commonService.calcTouta(shiyoulotHibetsuFile);
                  }
                });
                keishaHibetsuFile = {...keishaHibetsuFile, ...wasuuPropsForKeishaHibetsuFile};
                return keishaHibetsuFile;
              })
            )
          )
        );
      })
    );
  }

  /**
   * 鶏舎内の全ての飼養ロットの日別ファイルを取得
   *
   * @param keishaId
   * @param targetDate
   */
  getShiyoulotHibetsuFileInSameKeisha(keishaId: string, targetDate: string) {
    return this.db.collectionGroup(COLLECTION_NAME.HIBETSUFILE, (ref: firestore.Query) => {
      let query = ref;
      query = query.where('id', '==', targetDate);
      query = query.where('keishaId', '==', keishaId);
      return query;
    }).get();
  }

  /**
   * keishaHibetsuFileから
   * その日の水道消費量を計算
   * @param keishaHibetsuFile
   * @param keishaFloorArray
   */
  calcMeterDiff(keishaHibetsuFile: KeishaHibetsuFile, keishaFloorArray: number[]) {
    let meterDiff = 0; // TODO: メーターが最大値を超えて０に戻る場合に要対応;
    const keys = Object.keys(keishaHibetsuFile.suidouMeter);
    keishaFloorArray.forEach(floor => {
      const prop = {latest: METER_SHUBETSU.latest + floor, am: METER_SHUBETSU.am + floor, pm: METER_SHUBETSU.pm + floor};
      if (keys.includes(prop.latest)) {
        meterDiff -= keishaHibetsuFile.suidouMeter[prop.latest] ?? 0;
        if (keys.includes(prop.pm) && keishaHibetsuFile.suidouMeter[prop.pm]) {
          meterDiff += keishaHibetsuFile.suidouMeter[prop.pm];
        } else if (keys.includes(prop.am) && keishaHibetsuFile.suidouMeter[prop.am]) {
          meterDiff += keishaHibetsuFile.suidouMeter[prop.am];
        } else {
          meterDiff += keishaHibetsuFile.suidouMeter[prop.latest];
        }
      }
    });
    return meterDiff;
  }

  /**
   * その日の最高温度を取得
   * @param obj
   */
  getHighestTemperatureValue(obj: Temperature) {
    const tempVal = this.genNotOutSideTemperature(obj);
    const values = this.commonService.deleteUndefinedFromArray(Object.values(tempVal));
    if (!values.length) {
      return null;
    }
    return Math.max(...values);
  }

  /**
   * その日の最低温度を取得
   * @param obj
   */
  getLowestTemperatureValue(obj: Temperature) {
    const tempVal = this.genNotOutSideTemperature(obj);
    const values = this.commonService.deleteUndefinedFromArray(Object.values(tempVal));
    if (!values.length) {
      return null;
    }
    return Math.min(...values);
  }

  genNotOutSideTemperature(obj: Temperature) {
    const tempVal = {...obj};
    delete tempVal.highTemperatureOutSide;
    delete tempVal.lowTemperatureOutSide;
    return tempVal;
  }

  /**
   * 渡された鶏舎日別ファイルからその日の1羽あたりの給餌量(g/羽)を計算
   * @param keishaHibetsuFile
   */
  getShowKyuujiRyou(keishaHibetsuFile: KeishaHibetsuFile) {
    // 鶏舎日別ファイルの現在羽数は飼養ロット日別ファイルから算出しセットされている前提のため、
    // 鶏舎日別Fの斃死淘汰羽数は考慮しない。
    if (keishaHibetsuFile.hansouRyou && keishaHibetsuFile.genzaiWasuu) {
      return Math.round(keishaHibetsuFile.hansouRyou * KG_TO_G / keishaHibetsuFile.genzaiWasuu * 10) / 10;
    }
    return 0;
  }

  /**
   * 渡された鶏舎日別ファイルからその日の1羽あたりの給水量（ml/羽）を計算
   * @param keishaHibetsuFile
   * @param selectedKeisha
   */
  getShowKyuusuiRyou(keishaHibetsuFile: KeishaHibetsuFile, selectedKeisha: Keisha) {
    const floor = selectedKeisha.layout.floor ? selectedKeisha.layout.floor : 1;
    const keishaFloorArray = this.commonService.genSequentialNumber(floor);
    if (!keishaHibetsuFile.genzaiWasuu) {
      return 0;
    }
    // 鶏舎日別ファイルの現在羽数は飼養ロット日別ファイルから算出しセットされている前提のため、
    // 鶏舎日別Fの斃死淘汰羽数は考慮しない。
    const val = Math.round(this.calcMeterDiff(keishaHibetsuFile, keishaFloorArray) * (1000 * 1000) / keishaHibetsuFile.genzaiWasuu);
    return !isFinite(val) || isNaN(val) ? 0 : val;
  }

  /**
   * 「正常」以外の値がtrueかチェック
   * trueの値があれば、trueを返す
   * @param selectedKeishaHibetsuFile
   */
  checkIsAbnormalState(selectedKeishaHibetsuFile?: KeishaHibetsuFile | WithNullable<KeishaHibetsuFile, 'feed'>) {
    if (selectedKeishaHibetsuFile) {
      return this.commonService.keysOf(ABNORMAL_PROPERTIES)
        .filter(key => key !== SEIJOU)
        .map(key => Boolean(selectedKeishaHibetsuFile[key]))
        .reduce((boolPrev: boolean, boolCurrent: boolean) => boolPrev || boolCurrent);
    }
    return false;
  }

  /**
   * 「正常」が選択された場合、それ以外の異常プロパティをfalseにする
   * @param selectedKeishaHibetsuFile
   * @param selectedKey
   */
  resetAbnormalProperty(selectedKeishaHibetsuFile: KeishaHibetsuFile | WithNullable<KeishaHibetsuFile, 'feed'>,
    selectedKey: string) {
    if (!selectedKeishaHibetsuFile) {
      return;
    }
    const existSeijouAsKey = Object.keys(selectedKeishaHibetsuFile).some(key => key === SEIJOU);

    // 正常を選択した際はその他のプロパティをfalseに設定
    if (selectedKey === SEIJOU && existSeijouAsKey && selectedKeishaHibetsuFile[SEIJOU] === true) {
      this.commonService.keysOf(ABNORMAL_PROPERTIES).forEach(key => {
        if (key !== SEIJOU) {
          if (selectedKeishaHibetsuFile) {
            selectedKeishaHibetsuFile[key] = false;
          }
        }
      });
    }
  }

  /**
   * TAIOU_PROPERTIESのクリックされなかった方をfalseにする
   * @param selectedKeishaHibetsuFile
   * @param selectedKey
   */
  onClickTaiouProperty(selectedKeishaHibetsuFile: KeishaHibetsuFile | WithNullable<KeishaHibetsuFile, 'feed'>,
    selectedKey: string) {
    // TAIOU_PROPERTIESのクリックされなかった方をfalseにする
    for (const property of this.commonService.keysOf(TAIOU_PROPERTIES)) {
      if (property !== selectedKey) {
        if (selectedKeishaHibetsuFile) {
          selectedKeishaHibetsuFile[property] = false;
        }
      }
    }
  }

  /**
   * hansouMeterの値が空だったら初期値を入れる
   * @param selectedKeishaHibetsuFile
   */
  initMeterWhenEmpty(selectedKeishaHibetsuFile: KeishaHibetsuFile | WithNullable<KeishaHibetsuFile, 'feed'>) {
    const meter = selectedKeishaHibetsuFile?.hansouMeter;
    if (selectedKeishaHibetsuFile && !meter) {
      selectedKeishaHibetsuFile.hansouMeter = '00:00:00';
    }
  }

  /**
   * 搬送メータと鶏舎マスタの値から
   * 「搬送量」を計算する
   * @param selectedKeishaHibetsuFile
   * @param selectedKeisha
   */
  calcHansouRyou(selectedKeishaHibetsuFile: KeishaHibetsuFile | WithNullable<KeishaHibetsuFile, 'feed'>,
    selectedKeisha: Keisha) {
    if (!selectedKeishaHibetsuFile || !selectedKeishaHibetsuFile.hansouMeter) {
      return;
    }
    const hansouKadouMinute = this.commonService.calcHMStoMinutes(selectedKeishaHibetsuFile.hansouMeter);
    selectedKeishaHibetsuFile.hansouRyou
      = Math.round(hansouKadouMinute * selectedKeisha.esaHansouRyouPerMinute * 100) / 100;
  }

  /**
   * calcEsaZanがtrueの場合、
   * 「繰越」 = 「現在」 + 「入荷」-「給与」で計算
   */
  calcEsaKurikoshi(keishaHibetsuFile: KeishaHibetsuFile) {
    const genzai = keishaHibetsuFile.esaZan.esaZan1 ? keishaHibetsuFile.esaZan.esaZan1 : 0;
    const nyuuka = keishaHibetsuFile.esaNyuukaRyou ? keishaHibetsuFile.esaNyuukaRyou : 0;
    const kyuuyo = keishaHibetsuFile.hansouRyou ? keishaHibetsuFile.hansouRyou : 0;
    // TODO: 単位について注意!!! esaZanはkgに変更する?
    // ここにおいては全てkg単位の想定で実装。
    return genzai + nyuuka - kyuuyo;
  }

  /**
   * 水道メータの値が
   * 最大値を跨いだら0に
   * 最小値を跨いだら最大値に
   * @param selectedKeishaHibetsuFile
   * @param key
   */
  suidouMeterThroughMaxMin(selectedKeishaHibetsuFile: KeishaHibetsuFile | WithNullable<KeishaHibetsuFile, 'feed'>,
    key: string) {
    if (!selectedKeishaHibetsuFile) {
      return;
    }
    if (selectedKeishaHibetsuFile.suidouMeter[key] < 0) {
      selectedKeishaHibetsuFile.suidouMeter[key] = this.suidouMeterMax();
    } else if (selectedKeishaHibetsuFile.suidouMeter[key] > this.suidouMeterMax()) {
      selectedKeishaHibetsuFile.suidouMeter[key] = 0;
    }
  }

  /**
   *
   */
  suidouMeterStep() {return 0.01;}

  /**
   *
   */
  suidouMeterMax() {return 10000 - this.suidouMeterStep();}

  /**
   *
   * @param selectedKeisha
   */
  getFeedList(selectedKeisha: Keisha) {
    let feedShubetsuList = [];
    const activeFarm = this.masterService.farmMaster.find(farm => farm.id === selectedKeisha?.farmId);
    if (activeFarm?.feedIdList) {
      activeFarm.feedIdList.forEach((feedId: string) => {
        const feed = this.masterService.feedMaster[0].feedProgram.find((feed: Feed) => feedId === feed.id);
        feedShubetsuList.push(feed);
      });
    } else if (activeFarm?.feedList) {
      feedShubetsuList = activeFarm.feedList;
    }
    return feedShubetsuList;
  }

  /**
   * 渡された鶏舎日別ファイルの鶏の異常関連の値を結合した文字列にして返す
   * @param keishaHibetsuFile
   */
  getAbnormal(keishaHibetsuFile: KeishaHibetsuFile | undefined) {
    if (typeof keishaHibetsuFile === 'undefined') { return ''; }
    const abnormalKeys = this.commonService.keysOf(ABNORMAL_PROPERTIES);
    return abnormalKeys.reduce((prev: string, current) => {
      if (keishaHibetsuFile[current]) {
        // 複数のプロパティがtrueだった場合、空白を挟んで連結
        if (prev.length) { prev += ' '; }
        return prev + ABNORMAL_PROPERTIES[current];
      }
      return prev;
    }, '');
  }
}

/**
 *
 */
export function genTentouDefaultValue(): TentouShoutouJikoku[] {
  return [{start: '', end: ''}];
}

/**
 *
 */
export function genFeedDefaultValue(): Feed2 {
  return {id: '', feedShubetsu: '', ME: 0, cp: 0};
}

/**
 *
 * @param keishaId
 * @param today
 */
export function getInitKeishaHibetsuFile(keishaId: string, today: yyyyMMdd): KeishaHibetsuFile {
  return {
    kind: COLLECTION_NAME.KEISHAHIBETSUFILE,
    id: today,
    keishaId: keishaId,
    feed: genFeedDefaultValue(),
    temperature: {},
    tentouShoutouJikoku: genTentouDefaultValue(),
    warikomiTentouShoutouJikoku: genTentouDefaultValue(),
    esaZan: {},
    suidouMeter: {},
    seijou: false,
    ketsuben: false,
    naki: false,
    chinutsu: false,
    sonota: false,
    houkokuZumi: false,
    miHoukoku: false,
    taiouZumi: false,
    operation: '',
    operatorList: [],
  };
}

/**
 * 鶏舎日別ファイルに作業者の情報をセット
 * 既に追加されている作業者は追加しない
 * @param keishaHibetsuFile
 * @param operatorList
 */
export function setOperatorList(keishaHibetsuFile: KeishaHibetsuFile, operatorList: {name: string}[]) {
  if (!keishaHibetsuFile.operatorList) {
    keishaHibetsuFile.operatorList = [];
  }
  // operatorListにのみ存在する値を取得
  const filtered = operatorList.filter(operator => !keishaHibetsuFile.operatorList.find(r => r.name === operator.name));
  // keishaHibetsuFile.operatorListに足す。
  keishaHibetsuFile.operatorList.push(...filtered.map(r => ({name: r.name, worked: false})));
  return keishaHibetsuFile;
}
